import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { consumeReturnUrl } from "../utils/urlUtils";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  timeZone: string;
  firstName?: string;
  lastName?: string;
  title?: string; // Job title
  department?: string; // Department name
  profileImage?: string;
  customUrl?: string;
  username?: string;
  preferredLanguage?: 'en' | 'ja';
  // Profile settings
  country?: string;
  dateFormat?: string;
  timeFormat?: string;
  // Branding settings
  organizationName?: string;
  organizationId?: number;
  organizationRole?: 'owner' | 'admin' | 'member' | 'guest' | null; // User's role in their organization
  coverPhoto?: string;
  coverPhotoMobile?: string;
  hideSailLabBranding?: boolean;
  brandColor?: string;
  // Communication template settings
  defaultCommunicationTemplateGroup?: string;
  // Onboarding status
  onboardingCompleted?: boolean;
  // Organization calendar sharing
  allowOrgCalendarAccess?: boolean;
}

export interface AuthActionResult {
  success: boolean;
  /** Server error code, e.g. 'email_not_verified' | 'weak_password' */
  code?: string;
  /** True when a verification email was sent (identifier-first signup) */
  requiresVerification?: boolean;
  /** Set when the email already belongs to an account */
  existingAccount?: 'oauth' | 'password';
  /** OAuth providers linked to the existing account */
  providers?: Array<'google' | 'microsoft'>;
  /** Whether the existing OAuth account also has a password */
  hasPassword?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthActionResult>;
  register: (email: string) => Promise<AuthActionResult>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
  refreshToken: () => Promise<boolean>;
  updateCurrentUser: (updatedUser: User) => void;
  verifyToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: () => {},
  error: null,
  clearError: () => {},
  refreshToken: async () => false,
  updateCurrentUser: () => {},
  verifyToken: async () => false,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const clearError = () => setError(null);

  // Ref to track ongoing fetch requests and prevent duplicates
  const fetchUserDataRef = useRef<AbortController | null>(null);
  const lastSuccessfulFetchRef = useRef<string>("");
  // Ref to hold current token value for storage listener (avoids token in dependency array)
  const tokenRef = useRef<string | null>(token);

  // Function to fetch user data
  const fetchUserData = async (authToken: string, forceRefresh: boolean = false) => {
    try {
      // Validate token
      if (!authToken) {
        setUser(null);
        setIsAuthenticated(false);
        setToken(null);
        localStorage.removeItem("token");
        setError("No authentication token. Please login again.");
        setIsLoading(false);
        return;
      }

      // Prevent duplicate requests for the same token unless forced
      if (!forceRefresh && lastSuccessfulFetchRef.current === authToken) {
        return;
      }

      // Abort any existing request
      if (fetchUserDataRef.current) {
        fetchUserDataRef.current.abort();
      }

      // Create new abort controller
      fetchUserDataRef.current = new AbortController();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          signal: fetchUserDataRef.current.signal,
        }
      );

      if (!response.ok) {
        // If unauthorized, clear token and trigger re-login
        if (response.status === 401) {
          setUser(null);
          setIsAuthenticated(false);
          setToken(null);
          localStorage.removeItem("token");
          setError("Session expired. Please login again.");
          setIsLoading(false);
          navigate("/login");
          return;
        }

        throw new Error(`Failed to fetch user data: ${response.statusText}`);
      }

      const data = await response.json();

      // Ensure we have valid user data
      if (!data.user || !data.user.id) {
        setUser(null);
        setIsAuthenticated(false);
        setToken(null);
        localStorage.removeItem("token");
        setError("Invalid user data received. Please login again.");
        setIsLoading(false);
        navigate("/login");
        return;
      }

      setUser(data.user);
      setIsAuthenticated(true);
      setIsLoading(false);

      // Mark this token as successfully fetched
      lastSuccessfulFetchRef.current = authToken;
    } catch (error) {
      // Don't handle aborted requests as errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
      localStorage.removeItem("token");
      setError("Session expired. Please login again.");
      setIsLoading(false);
      navigate("/login");
    }
  };

  // Keep tokenRef in sync with token state
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Check for token on initial load only - avoid circular dependencies
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");

      if (storedToken) {
        setToken(storedToken);

        // Skip blocking auth API call on public booking pages (/:username/:slug)
        // Booking page only needs the token value (already set from localStorage above)
        const pathname = window.location.pathname;
        const segments = pathname.split('/').filter(Boolean);
        const knownAppPrefixes = [
          'admin', 'login', 'register', 'schedule', 'meeting-types',
          'integrations', 'availability', 'profile', 'settings', 'billing',
          'teams', 'contacts', 'analytics', 'onboarding', 'communication',
          'oauth', 'forgot-password', 'reset-password', 'verify-email', 'signup',
          'reschedule', 'cancel',
          // Poll pages are two segments (/poll/new, /poll/:publicId) and would
          // otherwise be read as booking pages, which defer the auth call. They
          // must not: both pages ASK whether the visitor is signed in — the
          // creator to decide whether the poll lands in an account, the answer
          // page to decide whether it is showing the host their own poll — and
          // an answer that arrives a moment later is an answer that arrives
          // after they have clicked. Guests are unaffected: with no stored
          // token this branch never runs.
          'poll'
        ];
        const isPublicBookingPage = segments.length === 2 && !knownAppPrefixes.includes(segments[0]);

        if (isPublicBookingPage) {
          // Don't block page load with auth API call
          setIsLoading(false);
          // Still fetch user data in background (non-blocking) for header display
          fetchUserData(storedToken, true);
        } else {
          await fetchUserData(storedToken, true); // Force initial fetch
        }
      } else {
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []); // Empty dependency array - only run on mount

  // Listen for storage events (when localStorage changes in another tab)
  // Uses tokenRef instead of token in dependency array to prevent re-registering listener
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to token changes — ignore i18next and other localStorage writes
      if (e.key !== "token") return;

      const newToken = e.newValue;

      if (newToken && newToken !== tokenRef.current) {
        setToken(newToken);
        fetchUserData(newToken, true);
      } else if (!newToken && tokenRef.current) {
        // Token was removed
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      // Clean up any ongoing fetch request
      if (fetchUserDataRef.current) {
        fetchUserDataRef.current.abort();
      }
    };
  }, []); // No dependencies — uses tokenRef for current value

  // UI language to localize auth emails (verification / reset)
  const currentLanguage = (): 'en' | 'ja' =>
    (localStorage.getItem('i18nextLng') || 'en').startsWith('ja') ? 'ja' : 'en';

  // Login function (email/password)
  // NOTE: never touch the shared `isLoading` here — PublicRoute unmounts the
  // Login/Signup page while it is true, wiping the page's local stage state.
  const login = async (email: string, password: string): Promise<AuthActionResult> => {
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        setToken(data.token);
        await fetchUserData(data.token, true);
        // New users (verified email but onboarding abandoned) resume onboarding
        const meResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          { headers: { Authorization: `Bearer ${data.token}` } }
        );
        const meData = meResponse.ok ? await meResponse.json() : null;
        if (meData?.user?.onboardingCompleted === false) {
          // Onboarding always wins — finish setup before honouring a deep link
          navigate("/onboarding");
        } else {
          // Return to the deep link that bounced them here, if any (see PrivateRoute)
          navigate(consumeReturnUrl() || "/schedule");
        }
        return { success: true };
      }

      // 'email_not_verified' is surfaced so the page can offer a resend button
      if (data.code !== 'email_not_verified') {
        setError(
          data.message || "Login failed. Please check your credentials."
        );
      }
      return { success: false, code: data.code };
    } catch (error) {
      setError("Login failed. Please try again.");
      return { success: false };
    }
  };

  // Register function — identifier-first: only the email is collected here.
  // Name + password are chosen on the page behind the verification link.
  // If the email already has an account, the result says which sign-in
  // method to route the user to.
  // NOTE: never touch the shared `isLoading` here (see login above).
  const register = async (email: string): Promise<AuthActionResult> => {
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, language: currentLanguage() }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          requiresVerification: data.requiresVerification,
          existingAccount: data.existingAccount,
          providers: data.providers,
          hasPassword: data.hasPassword,
        };
      }

      setError(data.message || "Registration failed. Please try again.");
      return { success: false, code: data.code };
    } catch (error) {
      setError("Registration failed. Please try again.");
      return { success: false };
    }
  };

  // Logout function — calls backend to clear server-side cache, then clears client state
  const logout = () => {
    const currentToken = localStorage.getItem("token");
    if (currentToken) {
      // Fire-and-forget: clear server-side user cache (don't block UI on response)
      fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login");
  };

  // Refresh token function
  const refreshToken = async (): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Check if there's a token to refresh
      const currentToken = localStorage.getItem("token");
      if (!currentToken) {
        setIsLoading(false);
        return false;
      }

      // Call refresh token endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        // Clear token if unauthorized
        if (response.status === 401) {
          setUser(null);
          setIsAuthenticated(false);
          setToken(null);
          localStorage.removeItem("token");
        }

        setIsLoading(false);
        return false;
      }

      const data = await response.json();

      if (data && data.token) {
        localStorage.setItem("token", data.token);
        setToken(data.token);
        await fetchUserData(data.token, true);
        setIsLoading(false);
        return true;
      } else {
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      setIsLoading(false);
      return false;
    }
  };

  // Function to update the current user
  const updateCurrentUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // Function to verify if the current token is valid
  const verifyToken = async (): Promise<boolean> => {
    try {
      const currentToken = localStorage.getItem("token");

      if (!currentToken) {
        return false;
      }

      // Create abort controller for this request
      const abortController = new AbortController();
      
      // Make a simple request that requires authentication
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        // If token is invalid, clear it
        if (response.status === 401) {
          setUser(null);
          setIsAuthenticated(false);
          setToken(null);
          localStorage.removeItem("token");
        }

        return false;
      }

      // Token is valid
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        register,
        logout,
        error,
        clearError,
        refreshToken,
        updateCurrentUser,
        verifyToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
